import * as React from "react";
import "./styles.scss";
import {AnimationContext} from "../../providers/AnimationProvider";
import {VeboxOverlay} from "../../../../../components/overlay/VeboxOverlay";
import {AnimatePresence} from "framer-motion";
import {MassiveActionsProcessHeader} from "../../../components/UtilsComponents";
import {ProcessStageOrder, useProcessController} from "../../../../Processes";
import {CensusStatusPage} from "../CensusStatusPage";
import {DefineEquipmentPageProvider} from "../../providers/DefineEquipmentPageProvider";
import {CensusCoolerContext} from "../../providers/CensusCollerProvider";
import {DefineAndProcessEquipmentStep} from "../DefineAndProcessEquipmentStep";
import {SuggestedActionsProvider} from "../../providers/SuggestedActionsProvider";
import {CensusSummaryPage} from "../CensusSummaryPage";
import {AdviseModal} from "../../components/AdviseModal";
import {FindEquipmentProvider} from "../../providers/FindEquipmentProvider";
import {isSecondStepCensus} from "../../utils";
import {Display} from "../../../../../components/Display";
import {DeleteOrContinueModal} from "../../components/DeleteOrContinueModal";

type NewCensusProcessProps = {};

export const NewCensusProcess: React.FC<NewCensusProcessProps> = () => {
    const {direction, props} = React.useContext(AnimationContext);
    const {
        census,
        AppTranslations,
        censusAvailability,
        isFromExistingCensus,
        fromCensusHistory,
        deleteCurrentCensusAndStartNew,
    } = React.useContext(CensusCoolerContext);
    const censusTranslation = AppTranslations.massiveActions.censusCooler;
    const title = censusTranslation.title;
    const texts = [
        censusTranslation.equipmentDefinition,
        censusTranslation.censusStatus,
        censusTranslation.summary,
    ];
    const [isAvailable, setIsAvailable] = React.useState<boolean>(true);
    const [canContinue, setCanContinue] = React.useState<boolean>(false);
    const [isDeletingCensus, setIsDeletingCensus] = React.useState<boolean>(false);

    const overlayController = VeboxOverlay.useTarget(<></>);

    const processController = useProcessController();

    const deleteAndStartNew = async () => {
        setIsDeletingCensus(true)
        await deleteCurrentCensusAndStartNew();
        overlayController.clear();
    }

    const goBackHome = () => {
        window.location.reload();
    };
    const goBack = () => {
        history.back()
    };

    const continueCensus = () => {
        setCanContinue(true)
        overlayController.clear();
    };

    React.useEffect(() => {
        setIsAvailable(censusAvailability?.data?.editable === true);
    }, [
        censusAvailability?.isLoading,
        censusAvailability?.error,
        censusAvailability?.data?.editable,
        censusAvailability?.data?.supported,
    ]);

    React.useEffect(() => {
        if (isAvailable === false) {
            overlayController.show(
                <AdviseModal
                    title={" "}
                    description={<p>{censusAvailability?.data?.errorMessage}</p>}
                    onClose={goBackHome}
                    cancelText={AppTranslations.genericWords.homeButton}
                />,
            );
        } else {
            overlayController.clear();
        }
    }, [isAvailable]);

    React.useEffect(() => {
        if (isFromExistingCensus) {
            overlayController.show(
                <DeleteOrContinueModal
                    title={AppTranslations.massiveActions.censusCooler.continueCensus}
                    onDelete={deleteAndStartNew}
                    onContinue={continueCensus}
                    deleteText={AppTranslations.massiveActions.censusCooler.deleteAndStartNew}
                    continueText={AppTranslations.massiveActions.censusCooler.continueCensus}
                    description={<p>{AppTranslations.massiveActions.censusCooler.yourCensusInProgress}</p>}
                    isDeleting={isDeletingCensus}
                    onXClick={fromCensusHistory ? goBack : goBackHome}
                />,
            );
        }
    }, [isFromExistingCensus]);

    React.useEffect(() => {
        if (isSecondStepCensus(census) && canContinue) {
            processController.goTo(1) //go to 2 step (start from 0)
        }
    }, [census, canContinue]);

    React.useEffect(() => {
        return () => {
            setCanContinue(false)
        }
    }, []);

    return (
        <>
            <AnimatePresence initial={false} custom={direction}>
                <MassiveActionsProcessHeader
                    processHeaderTitle={title}
                    processIndicatorsTitles={texts}
                />
                <DefineEquipmentPageProvider
                    census={census}
                    isFromExistingCensus={isFromExistingCensus && canContinue}
                >
                    <FindEquipmentProvider>
                        <VeboxOverlay.Target
                            {...overlayController.targetProps}
                            className={"process-stages flx-row"}
                            overlayClassName={"z-index-1"}
                        >
                            <Display isVisible={!isFromExistingCensus || (isFromExistingCensus && canContinue)}>
                                <DefineAndProcessEquipmentStep
                                    title={texts[0]}
                                    stepNumber={1}
                                    stageOrder={ProcessStageOrder.FIRST}
                                    divProps={props}
                                    overlayController={overlayController}
                                />
                                <SuggestedActionsProvider stepNumber={2}>
                                    <CensusStatusPage
                                        title={texts[1]}
                                        stepNumber={2}
                                        stageOrder={ProcessStageOrder.MIDDLE}
                                        divProps={props}
                                        overlayController={overlayController}
                                    />
                                    <CensusSummaryPage
                                        title={texts[2]}
                                        stepNumber={3}
                                        stageOrder={ProcessStageOrder.LAST}
                                        divProps={props}
                                    />
                                </SuggestedActionsProvider>
                            </Display>
                        </VeboxOverlay.Target>
                    </FindEquipmentProvider>
                </DefineEquipmentPageProvider>
            </AnimatePresence>
        </>
    );
};
